import {
  MEDICAL_ANSWER_BODY_MAX,
  MEETING_NOTICE_COPY,
  NOTICE_QUESTION_COPY,
  type MeetingNoticeCta,
} from "@care-by-ella/contracts";
import {
  MEETING_INFO_CTA_OPTIONS,
  meetingMessageCtaLabel,
  type MeetingMessageDraft,
} from "@/lib/meeting-notice";

// TES-1393 (2.1 S5; 09C 2026-10-13, Ö-561) — the body of "Om ditt vårdmöte"
// and "Förslag om extra vårdmöte" in "Skicka besked". Both are read under
// "Din vård → Vårdmöten", never as care notes, and have no answer field.
//
// The type names, the CTA labels and the technical-fault text are 09C's,
// verbatim; the staff-facing help lines are proposed copy.
export function MeetingMessageFields({
  draft,
  onChange,
}: {
  draft: MeetingMessageDraft;
  onChange: (draft: MeetingMessageDraft) => void;
}) {
  const proposal = draft.kind === "meeting_proposal";
  const ctaLabel = meetingMessageCtaLabel(draft);
  return (
    <>
      <p className="text-xs text-textSecondary">
        {proposal
          ? "Visas under Din vård → Vårdmöten, inte som vårdanteckning. Patienten får knappen Boka vårdmöte och en uppgift på Hem tills ett vårdmöte är bokat. Meddelandet markerar inte att mötet är bokat. Vill du boka en bestämd tid, använd Boka nytt vårdmöte."
          : "Praktisk information om ett tekniskt problem, ett inställt möte eller en ombokning. Visas under Din vård → Vårdmöten, inte som vårdanteckning."}
      </p>
      {proposal ? null : (
        <button
          type="button"
          onClick={() =>
            onChange({
              ...draft,
              body: MEETING_NOTICE_COPY.technicalFaultPreset,
            })
          }
          className="self-start text-xs underline"
        >
          Använd förinställd text vid tekniskt fel
        </button>
      )}
      <label className="flex flex-col gap-xs">
        <span>Meddelande till patienten</span>
        <textarea
          value={draft.body}
          onChange={(e) => onChange({ ...draft, body: e.target.value })}
          rows={5}
          maxLength={MEDICAL_ANSWER_BODY_MAX}
          className="rounded-md border border-surfaceMuted bg-background p-xs"
        />
      </label>
      {proposal ? null : (
        <label className="flex flex-col gap-xs">
          <span>Knapp för patienten</span>
          <select
            value={draft.cta}
            onChange={(e) =>
              onChange({
                ...draft,
                cta: e.target.value as MeetingNoticeCta | "",
              })
            }
            className="self-start rounded-md border border-surfaceMuted bg-background p-xs"
          >
            {MEETING_INFO_CTA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {draft.cta === "confirm_new_time" ? (
            <span className="text-xs text-textSecondary">
              Kräver en föreslagen tid som patienten inte har bekräftat ännu
              (Boka nytt vårdmöte).
            </span>
          ) : null}
        </label>
      )}
      <p className="text-xs font-medium" role="note">
        {NOTICE_QUESTION_COPY.cannotReply}
      </p>
      <details className="rounded-md border border-surfaceMuted bg-background p-xs text-xs">
        <summary className="cursor-pointer text-textSecondary">
          Förhandsvisning för patienten
        </summary>
        <div className="mt-xs flex flex-col gap-sm">
          <div>
            <p className="text-textSecondary">Notifiering</p>
            <p className="font-medium">{MEETING_NOTICE_COPY.pushTitle}</p>
            <p>{MEETING_NOTICE_COPY.pushBody}</p>
          </div>
          {proposal ? (
            <div>
              <p className="text-textSecondary">Uppgift på Hem</p>
              <p className="font-medium">
                {MEETING_NOTICE_COPY.proposalTask.title}
              </p>
              <p>{MEETING_NOTICE_COPY.proposalTask.body}</p>
              <p className="underline">
                {MEETING_NOTICE_COPY.proposalTask.ctaLabel}
              </p>
            </div>
          ) : null}
          <div>
            <p className="text-textSecondary">Under Din vård → Vårdmöten</p>
            <p className="font-medium">
              {MEETING_NOTICE_COPY.typeLabel[draft.kind]}
            </p>
            <p className="whitespace-pre-wrap">{draft.body.trim() || "…"}</p>
            {ctaLabel ? (
              <button
                type="button"
                disabled
                className="mt-xs self-start rounded-md bg-brand px-md py-xs text-textOnBrand opacity-60"
              >
                {ctaLabel}
              </button>
            ) : null}
          </div>
          <p className="text-textSecondary">
            Appar som ännu inte har Vårdmöten visar meddelandet som ett besked
            från läkaren, med samma notifiering som i dag.
          </p>
        </div>
      </details>
    </>
  );
}
